import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { MeetingRoom, MeetingRoomBooking } from "./MeetingRooms";
import { Moment } from "moment";
import {
  failureNotification,
  successNotification,
  warningNotification
} from '@shared/store/notification.duck';
import moment from "moment";
import {
  bookMeetingRoomV2,
  cancelMeetingRoomBookingV2,
  cancelOccurrenceMeetingRoomBookingV2,
  cancelOutlookMeetingBookingV2,
  updateMeetingRoomBookingV2
} from '@features/meeting-rooms/MeetingRoomV2Service';
import { refreshBookings } from "./room-finder.duck";
import { closeDialog } from '@shared/store/dialog.duck';
import { BasicUserInfo } from '@shared/models/user.models';
import {
  HospitalityBookingRequest,
  HospitalityOrderItem,
  HospitalityOrderItemWithQuantity,
  SpecialHospitalityItem
} from "./hospitality.models";
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { getHospitalityInfoFor, loadBookedHospitalityItemsForMeetingRoomBooking } from "./hospitality.http";
import { VisitorBooking } from "../../company-movements/components/visitor-booking/data/visitor-booking-service";
import { getErrorMessage } from '@shared/services/agent';

export enum MeetingRoomBookingTab {
  Booking, Attendees, Hospitality, Debug
}

export enum Recurrence {
  Daily = 'daily',
  Weekly = 'Weekly',
  Fortnightly = 'Fortnightly',
  RelativeMonthly = 'RelativeMonthly',
  RelativeYearly = 'RelativeYearly',
  AbsoluteMonthly = 'AbsoluteMonthly',
  AbsoluteYearly = 'AbsoluteYearly',
}

export enum MeetingRoomBookingViewPanel {
  MeetingRoomBooking, AddRemoveAttendees, AddRemoveVisitors,
  AddItemsFromMenu, AddInstructions, AddSpecialItems
}

export interface MeetingRoomBookingDialogState {
  loading: boolean;
  isSavingMeetingBooking: boolean;
  rootOffice?: OfficeEntity;
  activeBookingTab: MeetingRoomBookingTab;
  activePanel: MeetingRoomBookingViewPanel;
  meetingRoom?: MeetingRoom;
  meetingFromTime?: Moment;
  meetingToTime?: Moment;
  meetingName: string;
  meetingBodyText?: string;
  shouldAddToCalendar: boolean;
  attendees: BasicUserInfo[];
  visitors: VisitorBooking[];
  hospitalityInstructions?: string;
  specialHospitalityItems: SpecialHospitalityItem[];
  hospitalityMenuItems: HospitalityOrderItem[];
  orderedHospitalityMenuItems: HospitalityOrderItemWithQuantity[];
  existingMeetingRoomBooking?: MeetingRoomBooking;
  hospitalityDeliveryDateTime?: Moment;
  outlookBookingLink?: string;
  overriddenMeetingHost?: BasicUserInfo;
  isTeamsMeeting?: boolean;
  hospitalityCostCenter?: string;
  recurrence?: Recurrence;
  numberOfOccurrences?: number;
  recurrenceEndDate?: Moment;
  applyToSeries: boolean;
}

export const initialMeetingRoomBookingDialogState: MeetingRoomBookingDialogState = {
  loading: false,
  isSavingMeetingBooking: false,
  activeBookingTab: MeetingRoomBookingTab.Booking,
  activePanel: MeetingRoomBookingViewPanel.MeetingRoomBooking,
  meetingName: '',
  shouldAddToCalendar: true,
  attendees: [],
  visitors: [],
  specialHospitalityItems: [],
  hospitalityMenuItems: [],
  orderedHospitalityMenuItems: [],
  isTeamsMeeting: false,
  applyToSeries: false,
}

function createHospitalityRequest(state: MeetingRoomBookingDialogState): HospitalityBookingRequest {
  const hospitalityBookingRequest: HospitalityBookingRequest = {
    instructions: state.hospitalityInstructions,
    deliveryDateTime: state.hospitalityDeliveryDateTime ? state.hospitalityDeliveryDateTime.format('HH:mm') : state.meetingFromTime?.format('HH:mm') ?? undefined,
    menuItems: state.orderedHospitalityMenuItems.map(i => ({quantity: i.quantity, id: i.menuItemId || 0})),
    specialMenuItems: state.specialHospitalityItems,
    visitors: state.visitors,
    attendees: state.attendees.map(u => u.userId),
    costCenter: state.hospitalityCostCenter,
  }

  return hospitalityBookingRequest;
}

function hasHospitalitySet(state: MeetingRoomBookingDialogState): boolean {
  return state.orderedHospitalityMenuItems.length > 0 ||
    state.specialHospitalityItems.length > 0 ||
    !!state.hospitalityInstructions
}

const loadHospitalityMenuItems: any = createAsyncThunk(
  'meetingRoomBookingDialog/loadHospitalityMenuItems',
  async (params: {rootOffice: OfficeEntity}, thunkAPI) => {
    return (await getHospitalityInfoFor(params.rootOffice)).orderItems;
  }
)

const loadBookedHospitalityItems: any = createAsyncThunk(
  'meetingRoomBookingDialog/loadBookedHospitalityItems',
  async (params: {rootOffice: OfficeEntity, meetingRoomBooking: MeetingRoomBooking}, thunkAPI) => {
    if (params.rootOffice.hospitalityEnabled && params.meetingRoomBooking.teamTodayMeetingId) {
      const response = await loadBookedHospitalityItemsForMeetingRoomBooking(params.meetingRoomBooking.teamTodayMeetingId);

      let deliveryTime = moment(params.meetingRoomBooking.startDateTime);
      if (response.deliveryDateTime && response.deliveryDateTime.includes(':') && response.deliveryDateTime.split(':').length === 2) {
        deliveryTime = deliveryTime.hours(parseInt(response.deliveryDateTime.split(':')[0])).minutes(parseInt(response.deliveryDateTime.split(':')[1]));
      }

      return {
        attendees: response.attendees.map((a: any) => a.user),
        visitors: response.visitors,
        specialHospitalityItems: response.specialOrderItems,
        orderedItems: response.orderItems,
        instructions: response.instructions,
        deliveryDateTime: deliveryTime,
        hospitalityCostCenter: response.costCenter,
      }
    }
  }
)

export const initMeetingRoomBookingDialog: any = createAsyncThunk(
  'meetingRoomBookingDialog/initMeetingRoomBookingDialog',
  async (params: {meetingRoom: MeetingRoom, activeDay: Moment, hour: number, minutes: number, rootOffice: OfficeEntity, meetingBooking: MeetingRoomBooking}, thunkAPI) => {
    if (params.rootOffice.hospitalityEnabled) {
      thunkAPI.dispatch(loadHospitalityMenuItems({rootOffice: params.rootOffice}));
    }

    if (params.meetingBooking) {
      thunkAPI.dispatch(loadBookedHospitalityItems({rootOffice: params.rootOffice, meetingRoomBooking: params.meetingBooking}))
      const retVal = {
        rootOffice: params.rootOffice,
        meetingRoom: params.meetingRoom,
        hour: moment(params.meetingBooking.startDateTime).hour(),
        minutes: moment(params.meetingBooking.startDateTime).minutes(),
        meetingFromTime: moment(params.meetingBooking.startDateTime),
        hospitalityDeliveryDateTime: moment(params.meetingBooking.startDateTime),
        meetingToTime: moment(params.meetingBooking.endDateTime),
        existingMeetingRoomBooking: params.meetingBooking,
        meetingName: params.meetingBooking.teamTodayMeetingBookingLabel || params.meetingBooking.name,
        outlookBookingLink: params.meetingBooking.outlookEventWebLink,
        meetingBodyText: params.meetingBooking.bodyText,
        isTeamsMeeting: params.meetingBooking.teamsMeeting,
      }
      return retVal;
    } else {
      return {
        rootOffice: params.rootOffice,
        meetingRoom: params.meetingRoom,
        hour: params.hour,
        minutes: params.minutes,
        meetingFromTime: params.activeDay.clone().hour(params.hour).minutes(params.minutes),
        hospitalityDeliveryDateTime: params.activeDay.clone().hour(params.hour).minutes(params.minutes),
        meetingToTime: params.activeDay.clone().hour(params.hour).minutes(params.minutes).add(30, 'minutes'),
        existingMeetingRoomBooking: params.meetingBooking,
      }
    }
  }
)

export const addVisitor: any = createAsyncThunk(
  'meetingRoomBookingDialog/addVisitor',
  async (params: {visitor: VisitorBooking}, thunkAPI) => {
    const visitors = (thunkAPI.getState() as AppState).meetingRoomBookingDialog.visitors;
    if (!visitors.find(v => v.visitorName === params.visitor.visitorName && v.companyName === params.visitor.companyName)) {
      return [
        ...visitors,
        params.visitor,
      ]
    }
    return visitors;
  }
)

export const addSpecialHospitalityItem: any = createAsyncThunk(
  'meetingRoomBookingDialog/addSpecialHospitalityItem',
  async (params: {item: SpecialHospitalityItem}, thunkAPI) => {
    const items = (thunkAPI.getState() as AppState).meetingRoomBookingDialog.specialHospitalityItems;
    return [...items, params.item];
  }
)

export const updateBooking: any = createAsyncThunk(
  'meetingRoomBookingDialog/updateBooking',
  async (params: {item: SpecialHospitalityItem}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState).meetingRoomBookingDialog;
    const meetingRoom = state.meetingRoom;
    const booking = state.existingMeetingRoomBooking;
    if (!booking || !meetingRoom) {
      throw new Error('No booking, must use create');
    }

    if (state.recurrence && !state.recurrenceEndDate) {
      thunkAPI.dispatch(warningNotification('Recurring meetings require an end date'))
      return;
    }

    if (hasHospitalitySet(state)) {
      if (!state.hospitalityDeliveryDateTime) {
        thunkAPI.dispatch(warningNotification('Delivery time required'))
        return;
      }
    }

    try {
      const hospitalityBookingRequest: HospitalityBookingRequest = createHospitalityRequest(state);
      const hasMeetingBookingId = !!state.existingMeetingRoomBooking?.teamTodayMeetingId;
      if (!hasMeetingBookingId) {
        thunkAPI.dispatch(warningNotification('Cannot save, no booking id found.'))
        return;
      } else {

        let fromTime = moment(booking.startDateTime);
        let toTime = moment(booking.endDateTime);
        if (state.meetingFromTime) {
          fromTime = fromTime.minutes(state.meetingFromTime.minutes()).hour(state.meetingFromTime.hour());
        }
        if (state.meetingToTime) {
          toTime = toTime.minutes(state.meetingToTime.minutes()).hour(state.meetingToTime.hour());
        }

        await updateMeetingRoomBookingV2(state?.existingMeetingRoomBooking?.teamTodayMeetingId ?? 0,
          meetingRoom,
          fromTime,
          toTime,
          state.meetingName,
          !!meetingRoom.microsoftRoomId,
          !!state.isTeamsMeeting,
          hospitalityBookingRequest,
          state.overriddenMeetingHost,
          state.meetingBodyText,
          state.recurrence,
          state.recurrenceEndDate,
          !!state.existingMeetingRoomBooking?.isRecurringMeeting && state.applyToSeries,);
      }
      thunkAPI.dispatch(refreshBookings());
      thunkAPI.dispatch(closeDialog());
      thunkAPI.dispatch(successNotification('Meeting room booked'));
    } catch (e: any) {
      thunkAPI.dispatch(failureNotification(getErrorMessage(e, 'Failed booking meeting room')));
    }
  }
)

export const cancelBooking: any = createAsyncThunk(
  'meetingRoomBookingDialog/cancelBooking',
  async (params: {}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState).meetingRoomBookingDialog;
    const booking = state.existingMeetingRoomBooking;
    if (booking) {
      if (booking.teamTodayMeetingId) {

        try {
          if (booking?.isRecurringMeeting && booking.outlookBooking && booking.outlookOccurrenceId && booking.teamTodayMeetingId) {
            await cancelOccurrenceMeetingRoomBookingV2(booking.outlookOccurrenceId, booking.teamTodayMeetingId);
          } else {
            await cancelMeetingRoomBookingV2(booking.teamTodayMeetingId);
          }
          await new Promise((resolve: any) => setTimeout(() => resolve(), 500));
          await thunkAPI.dispatch(refreshBookings());
        } catch (e) {
          throw e;
        } finally {
          thunkAPI.dispatch(closeDialog());
        }

      } else if (booking.outlookEventUid && booking.outlookMeetingRoomId) {

        try {
          await cancelOutlookMeetingBookingV2(booking.outlookMeetingRoomId, booking.outlookEventUid);
          await new Promise((resolve: any) => setTimeout(() => resolve(), 500));
          await thunkAPI.dispatch(refreshBookings());
        } catch (e) {
          throw e;
        } finally {
          thunkAPI.dispatch(closeDialog());
        }
      }
    }
  }
)

export const cancelSeriesBooking: any = createAsyncThunk(
  'meetingRoomBookingDialog/cancelSeriesBooking',
  async (params: {}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState).meetingRoomBookingDialog;
    const booking = state.existingMeetingRoomBooking;
    if (!booking) {
      return;
    }
    if (booking.teamTodayMeetingId) {
      // seriesMasterId lets the backend cancel the Outlook series even when no calendar mapping exists
      await cancelMeetingRoomBookingV2(booking.teamTodayMeetingId, booking.outlookSeriesMasterId);
    } else if (booking.outlookSeriesMasterId && booking.outlookMeetingRoomId) {
      // Series booked directly in Outlook with no Team Today record: cancel the series master itself
      await cancelOutlookMeetingBookingV2(booking.outlookMeetingRoomId, booking.outlookSeriesMasterId);
    } else {
      return;
    }
    await new Promise((resolve: any) => setTimeout(() => resolve(), 500));
    await thunkAPI.dispatch(refreshBookings());
    thunkAPI.dispatch(closeDialog());
  }
)

// @ts-ignore
export const confirmBooking: any = createAsyncThunk(
  'meetingRoomBookingDialog/confirmBooking',
  async (params: {isOutlookRoom: boolean, calendarSyncActive: boolean, activeDay: Moment}, thunkAPI) => {
    const state = (thunkAPI.getState() as AppState).meetingRoomBookingDialog;
    const meetingRoom = state.meetingRoom;

    if (state.existingMeetingRoomBooking?.teamTodayMeetingId) {
      thunkAPI.dispatch(warningNotification('Is already booked, must update'))
      return
    }

    if (!state.meetingName) {
      thunkAPI.dispatch(warningNotification('Requires a meeting title'))
      return;
    }

    if (state.recurrence && !state.recurrenceEndDate) {
      thunkAPI.dispatch(warningNotification('Recurring meetings require an end date'))
      return;
    }

    if (hasHospitalitySet(state)) {
      if (!state.hospitalityDeliveryDateTime) {
        thunkAPI.dispatch(warningNotification('Delivery time required'))
        return;
      }
    }

    if (!meetingRoom) {
      return;
    }

    if (params.isOutlookRoom && !params.calendarSyncActive) {
      thunkAPI.dispatch(warningNotification('Cannot book this meeting room without calendar permissions.'));
      return;
    }

    const fromTime = state.meetingFromTime;
    const toTime = state.meetingToTime;
    if (!params.activeDay || !fromTime || !toTime) {
      thunkAPI.dispatch(warningNotification('No date or times identified'));
      return;
    }

    const bookingFromDateTime = params.activeDay.clone();
    const bookingToDateTime = params.activeDay.clone();
    bookingFromDateTime.hour(fromTime.hour());
    bookingFromDateTime.minutes(fromTime.minutes());
    bookingFromDateTime.seconds(0);
    bookingFromDateTime.milliseconds(0);
    bookingToDateTime.hour(toTime.hour());
    bookingToDateTime.minutes(toTime.minutes());
    bookingToDateTime.seconds(0);
    bookingToDateTime.milliseconds(0);


    if (bookingToDateTime.isBefore(moment())) {
      thunkAPI.dispatch(failureNotification('Cannot book meeting room in the past'));
      throw new Error('Cannot book meeting room in the past');
    }

    try {
      const hospitalityBookingRequest: HospitalityBookingRequest = createHospitalityRequest(state);
      // @ts-ignore
      const outlookEventUid = state.existingMeetingRoomBooking?.outlookEventUid;
      await bookMeetingRoomV2(meetingRoom,
        bookingFromDateTime,
        bookingToDateTime,
        state.meetingName,
        state.shouldAddToCalendar,
        !!state.isTeamsMeeting,
        hospitalityBookingRequest,
        state.overriddenMeetingHost,
        state.meetingBodyText,
        outlookEventUid,
        state.recurrence,
        state.recurrenceEndDate,
        !!state.recurrence && state.applyToSeries,
      );
      thunkAPI.dispatch(refreshBookings());
      thunkAPI.dispatch(closeDialog());
      thunkAPI.dispatch(successNotification('Meeting room booked'));
    } catch (e: any) {
      thunkAPI.dispatch(failureNotification(getErrorMessage(e, 'Failed booking meeting room')));
    }
  }
)

const meetingRoomBookingDialogSlice = createSlice({
  name: 'meetingRoomBookingDialog',
  initialState: initialMeetingRoomBookingDialogState,
  reducers: {
    reset: () => initialMeetingRoomBookingDialogState,
    setActiveBookingTab: (state, action) => ({...state, activeBookingTab: action.payload}),
    setDialogPanel: (state, action) => ({...state, activePanel: action.payload}),
    setMeetingName: (state, action) => ({...state, meetingName: action.payload}),
    setShouldAddToCalendar: (state, action) => ({...state, shouldAddToCalendar: action.payload}),
    setMeetingFromTime: (state, action) => ({...state, meetingFromTime: action.payload}),
    setMeetingToTime: (state, action) => ({...state, meetingToTime: action.payload}),
    addAttendee: (state, action) => ({...state, attendees: [...state.attendees, action.payload]}),
    setAttendees: (state, action) => ({...state, attendees: action.payload}),
    setHospitalityInstructions: (state, action) => ({...state, hospitalityInstructions: action.payload}),
    setHospitalityMenuItems: (state, action) => ({...state, hospitalityMenuItems: action.payload}),
    setOrderedHospitalityMenuItems: (state, action) => ({...state, orderedHospitalityMenuItems: action.payload}),
    setSpecialItems: (state, action) => ({...state, specialHospitalityItems: action.payload}),
    setDeliveryDateTime: (state, action) => ({...state, hospitalityDeliveryDateTime: action.payload}),
    setOverriddenMeetingHost: (state, action) => ({...state, overriddenMeetingHost: action.payload}),
    setMeetingBodyText: (state, action) => ({...state, meetingBodyText: action.payload}),
    setIsTeamsMeeting: (state, action) => ({...state, isTeamsMeeting: action.payload}),
    setHospitalityCostCenter: (state, action) => ({...state, hospitalityCostCenter: action.payload}),
    setRecurrence: (state, action) => ({...state, recurrence: action.payload, applyToSeries: !!action.payload}),
    setNumberOfOccurrences: (state, action) => ({...state, numberOfOccurrences: action.payload}),
    setRecurrenceEndDate: (state, action) => ({...state, recurrenceEndDate: action.payload}),
    setApplyToSeries: (state, action) => ({...state, applyToSeries: action.payload}),
    removeAttendee: (state, action) =>
      ({...state, attendees: [...state.attendees.filter(a => a.userId !== action.payload.userId)]}),
    removeVisitor: (state, action) =>
      ({...state, visitors: [...state.visitors.filter(a => a.companyName !== action.payload.companyName && a.visitorName !== action.payload.visitorName)]}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(initMeetingRoomBookingDialog.pending, (state) => ({...state, loading: true,}))
      .addCase(initMeetingRoomBookingDialog.rejected, (state) => ({...state, loading: false,}))
      .addCase(initMeetingRoomBookingDialog.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        meetingRoom: action.payload.meetingRoom,
        minutes: action.payload.minutes,
        teamTodayMeetingBookingId: action.payload.teamTodayMeetingBookingId,
        meetingFromTime: action.payload.meetingFromTime,
        meetingToTime: action.payload.meetingToTime,
        hour: action.payload.hour,
        rootOffice: action.payload.rootOffice,
        activePane: MeetingRoomBookingViewPanel.MeetingRoomBooking,
        activeBookingTab: MeetingRoomBookingTab.Booking,
        existingMeetingRoomBooking: action.payload.existingMeetingRoomBooking,
        meetingBodyText: (action.payload?.existingMeetingRoomBooking?.bodyText ?? ''),
        outlookBookingLink: action.payload.outlookBookingLink,
        meetingName: action.payload.meetingName || '',
        isTeamsMeeting: action.payload.isTeamsMeeting,
      }
    })
      .addCase(confirmBooking.pending, (state) => ({...state, isSavingMeetingBooking: true,}))
      .addCase(confirmBooking.rejected, (state) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(confirmBooking.fulfilled, (state, action) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(updateBooking.pending, (state) => ({...state, isSavingMeetingBooking: true, loading: true,}))
      .addCase(updateBooking.rejected, (state) => ({...state, isSavingMeetingBooking: false, loading: false,}))
      .addCase(updateBooking.fulfilled, (state, action) => ({...state, isSavingMeetingBooking: false, loading: false,}))
      .addCase(cancelBooking.pending, (state) => ({...state, isSavingMeetingBooking: true,}))
      .addCase(cancelBooking.rejected, (state) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(cancelBooking.fulfilled, (state, action) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(cancelSeriesBooking.pending, (state) => ({...state, isSavingMeetingBooking: true,}))
      .addCase(cancelSeriesBooking.rejected, (state) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(cancelSeriesBooking.fulfilled, (state, action) => ({...state, isSavingMeetingBooking: false,}))
      .addCase(addVisitor.fulfilled, (state, action) => ({...state, visitors: action.payload,}))
      .addCase(addSpecialHospitalityItem.fulfilled, (state, action) => ({...state, specialHospitalityItems: action.payload,}))
      .addCase(loadHospitalityMenuItems.fulfilled, (state, action) => ({...state, hospitalityMenuItems: action.payload,}))
      .addCase(loadBookedHospitalityItems.fulfilled, (state, action) => ({...state,
      attendees: action.payload?.attendees ?? [],
      visitors: action.payload?.visitors ?? [],
      specialHospitalityItems: action.payload?.specialHospitalityItems ?? [],
      orderedHospitalityMenuItems: action.payload?.orderedItems ?? [],
      hospitalityInstructions: action.payload?.instructions ?? '',
      hospitalityDeliveryDateTime: action.payload?.deliveryDateTime,
      hospitalityCostCenter: action.payload?.hospitalityCostCenter,
    }));
  }
})

export default meetingRoomBookingDialogSlice.reducer;
export const { reset, setActiveBookingTab, setDialogPanel, setMeetingName, setIsTeamsMeeting, setMeetingFromTime, setMeetingToTime, setAttendees, removeAttendee, removeVisitor, setHospitalityInstructions, setOrderedHospitalityMenuItems, setSpecialItems, setDeliveryDateTime, setOverriddenMeetingHost, setMeetingBodyText, setHospitalityCostCenter, setRecurrence, setRecurrenceEndDate, setApplyToSeries } = meetingRoomBookingDialogSlice.actions;

// Selectors
export const selectIsMeetingRoomBookingDialogBookingsLoading = (state: AppState) => state.meetingRoomBookingDialog.loading;
export const selectIsSavingMeetingBooking = (state: AppState) => state.meetingRoomBookingDialog.isSavingMeetingBooking;
export const selectActiveBookingTab = (state: AppState) => state.meetingRoomBookingDialog.activeBookingTab;
export const selectActivePanel = (state: AppState) => state.meetingRoomBookingDialog.activePanel;
export const selectMeetingRoom = (state: AppState) => state.meetingRoomBookingDialog.meetingRoom;
export const selectMeetingFromTime = (state: AppState) => state.meetingRoomBookingDialog.meetingFromTime;
export const selectMeetingToTime = (state: AppState) => state.meetingRoomBookingDialog.meetingToTime;
export const selectMeetingName = (state: AppState) => state.meetingRoomBookingDialog.meetingName;
const selectShouldAddToCalendar = (state: AppState) => state.meetingRoomBookingDialog.shouldAddToCalendar;
export const selectAttendees = (state: AppState) => state.meetingRoomBookingDialog.attendees;
export const selectVisitors = (state: AppState) => state.meetingRoomBookingDialog.visitors;
export const selectRootOfficeForMeetingRoom = (state: AppState) => state.meetingRoomBookingDialog.rootOffice;
export const selectHospitalityInstructions = (state: AppState) => state.meetingRoomBookingDialog.hospitalityInstructions;
export const selectSpecialHospitalityItems = (state: AppState) => state.meetingRoomBookingDialog.specialHospitalityItems;
export const selectHospitalityMenuItems = (state: AppState) => state.meetingRoomBookingDialog.hospitalityMenuItems;
export const selectOrderedHospitalityMenuItems = (state: AppState) => state.meetingRoomBookingDialog.orderedHospitalityMenuItems;
export const selectExistingMeetingRoomBooking = (state: AppState) => state.meetingRoomBookingDialog.existingMeetingRoomBooking;
export const selectDeliveryDateTime = (state: AppState) => state.meetingRoomBookingDialog.hospitalityDeliveryDateTime;
export const selectOutlookBookingLink = (state: AppState) => state.meetingRoomBookingDialog.outlookBookingLink;
export const selectOverriddenMeetingHost = (state: AppState) => state.meetingRoomBookingDialog.overriddenMeetingHost;
export const selectMeetingBodyText = (state: AppState) => state.meetingRoomBookingDialog.meetingBodyText;
export const selectIsTeamsMeeting = (state: AppState) => state.meetingRoomBookingDialog.isTeamsMeeting;
export const selectHospitalityCostCenter = (state: AppState) => state.meetingRoomBookingDialog.hospitalityCostCenter;
export const selectRecurrence = (state: AppState) => state.meetingRoomBookingDialog.recurrence;
const selectNumberOfOccurrences = (state: AppState) => state.meetingRoomBookingDialog.numberOfOccurrences;
export const selectRecurrenceEndDate = (state: AppState) => state.meetingRoomBookingDialog.recurrenceEndDate;
export const selectApplyToSeries = (state: AppState) => state.meetingRoomBookingDialog.applyToSeries;
